import React from 'react';
import { WebsiteDesignProps } from './types';
import { Sprout, Phone, Mail, ArrowRight, Flower2 } from 'lucide-react';
import { Button } from "@/components/ui/button";

const BiophilicHarmony: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Eden Grove Academy";

    return (
        <div className="bg-[#f2f4f1] text-[#2f3e2e] min-h-screen font-sans selection:bg-[#8fbc8f] selection:text-white">
            <header className="fixed w-full z-50 transition-all bg-[#f2f4f1]/80 backdrop-blur-md">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-2 group cursor-pointer">
                        <div className="w-10 h-10 bg-[#4a5d48] rounded-full flex items-center justify-center text-[#e8ece6] transition-transform group-hover:scale-110">
                            <Sprout size={20} />
                        </div>
                        <span className="text-xl font-bold tracking-tight text-[#2f3e2e] group-hover:text-[#4a5d48] transition-colors">{schoolName}</span>
                    </div>
                    
                    <nav className="hidden md:flex gap-8 text-sm font-medium text-[#5a6b59]">
                        {['Our Ethics', 'Learning Spaces', 'Admissions', 'Community'].map((item) => (
                            <a key={item} href="#" className="hover:text-[#2f3e2e] relative after:content-[''] after:absolute after:w-0 after:h-0.5 after:bg-[#4a5d48] after:bottom-[-4px] after:left-0 hover:after:w-full after:transition-all">{item}</a>
                        ))}
                    </nav>

                    <Button className="rounded-full bg-[#4a5d48] hover:bg-[#3a4d38] text-white px-6">Apply Now</Button>
                </div>
            </header>

            <section className="relative pt-32 pb-20 overflow-hidden">
                {/* Organic Shape Background */}
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#dbe4d8] rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/4" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#e6ede4] rounded-full blur-3xl opacity-60 translate-y-1/4 -translate-x-1/4" />

                <div className="container mx-auto px-6 relative z-10 flex flex-col items-center text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#c5d6c4] text-[#4a5d48] text-xs font-bold uppercase tracking-widest mb-8 shadow-sm animate-fade-in-up">
                        <Flower2 size={14} /> Growing Minds Naturally
                    </div>
                    
                    <h1 className="text-5xl md:text-7xl font-bold text-[#1a2619] mb-8 leading-tight max-w-4xl tracking-tight">
                        Deeply Rooted in <br/>
                        <span className="text-[#5a8c5a] font-serif italic">Knowledge & Nature</span>
                    </h1>
                    
                    <p className="text-lg text-[#5a6b59] max-w-2xl mx-auto mb-12 leading-relaxed">
                        We integrate ecological wisdom with academic rigor. Our campus is a living classroom where students connect with the environment while mastering 21st-century skills.
                    </p>

                    <div className="relative w-full max-w-5xl group">
                         <div className="absolute inset-0 bg-[#4a5d48] rounded-[3rem] rotate-2 opacity-10 group-hover:rotate-1 transition-transform duration-700" />
                         <img 
                            src="https://images.unsplash.com/photo-1588072432836-e10032774350?q=80&w=2072&auto=format&fit=crop" 
                            className="relative rounded-[3rem] shadow-xl w-full h-[500px] object-cover border-4 border-white transition-transform duration-700 group-hover:-translate-y-2" 
                            alt="Green Campus" 
                        />
                         <div className="absolute bottom-10 right-10 bg-white/90 backdrop-blur p-6 rounded-2xl shadow-lg max-w-xs text-left hidden md:block">
                             <div className="text-sm font-bold text-[#2f3e2e] mb-1">Architecture Award 2025</div>
                             <div className="text-xs text-[#5a6b59]">Recognized for sustainable campus design and student wellbeing.</div>
                         </div>
                    </div>
                </div>
            </section>

            <section className="bg-white py-24">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-[#2f3e2e]">
                        <div className="space-y-4">
                            <h3 className="text-2xl font-serif italic text-[#4a5d48]">Air quality & Focus</h3>
                            <p className="text-gray-600 leading-relaxed">Our classrooms are filled with oxygen-rich plants and natural light, proven to boost concentration and reduce stress.</p>
                            <a href="#" className="text-[#4a5d48] font-bold text-sm border-b border-[#4a5d48] pb-0.5 inline-block">Learn about our design</a>
                        </div>
                        <div className="space-y-4">
                            <h3 className="text-2xl font-serif italic text-[#4a5d48]">Outdoor Learning</h3>
                            <p className="text-gray-600 leading-relaxed">30% of the curriculum takes place outside. Physics in the playground, Biology in the garden, Literature under the oaks.</p>
                            <a href="#" className="text-[#4a5d48] font-bold text-sm border-b border-[#4a5d48] pb-0.5 inline-block">View Curriculum</a>
                        </div>
                        <div className="space-y-4">
                            <h3 className="text-2xl font-serif italic text-[#4a5d48]">Sustainable Food</h3>
                            <p className="text-gray-600 leading-relaxed">Farm-to-table lunches prepared by chefs using organic produce grown right here by our students.</p>
                            <a href="#" className="text-[#4a5d48] font-bold text-sm border-b border-[#4a5d48] pb-0.5 inline-block">See Sample Menu</a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BiophilicHarmony;
